import { Box, Divider } from '@mui/material';
import { ReportFormSectionSubheader } from 'components/dataCreationForms/report/formUi/presentational';
import { SubheaderText } from 'components/general';
import { inspectionFormSectionDividerSx } from 'components/inspection/layout';
import { FormattedMessage } from 'intl';
import type { TranslationKey } from 'intl/types/translationKey';
import React, { PropsWithChildren, ReactNode } from 'react';
import {
  dialogSectionDividerSx,
  dialogSectionGridSx,
  dialogSectionHeaderSx,
  dialogSectionSx,
  firstSectionHeaderSx,
  sectionGridSx,
  sectionHeaderSx,
  sectionTitleSx,
} from './styles';

interface CompanyFormSectionProps {
  titleId: TranslationKey;
  isDialog: boolean;
  isFirst?: boolean;
  titleAction?: ReactNode;
}

/** A titled two-column section, laid out like the inspection and template forms. */
function CompanyPageSection({
  titleId,
  isFirst,
  titleAction,
  children,
}: PropsWithChildren<CompanyFormSectionProps>): React.ReactElement {
  return (
    <>
      {!isFirst && <Divider sx={inspectionFormSectionDividerSx} />}
      <Box sx={isFirst ? firstSectionHeaderSx : sectionHeaderSx}>
        <SubheaderText sx={sectionTitleSx}>
          <FormattedMessage id={titleId} />
        </SubheaderText>
        {titleAction}
      </Box>
      <Box sx={sectionGridSx}>{children}</Box>
    </>
  );
}

/** A section laid out like the send PDF report dialog: the first one untitled, the next ones below an edge-to-edge divider. */
function CompanyDialogSection({
  titleId,
  isFirst,
  titleAction,
  children,
}: PropsWithChildren<CompanyFormSectionProps>): React.ReactElement {
  return (
    <Box sx={isFirst ? undefined : dialogSectionSx}>
      {!isFirst && (
        <>
          <Divider sx={dialogSectionDividerSx} />
          <Box sx={dialogSectionHeaderSx}>
            <ReportFormSectionSubheader messageId={titleId} />
            {titleAction}
          </Box>
        </>
      )}
      <Box sx={dialogSectionGridSx}>{children}</Box>
    </Box>
  );
}

export function CompanyFormSection(
  props: PropsWithChildren<CompanyFormSectionProps>
): React.ReactElement {
  return props.isDialog ? (
    <CompanyDialogSection {...props} />
  ) : (
    <CompanyPageSection {...props} />
  );
}
